import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
} from '@mui/material';
import {
  MergeType as MergeIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  Send as SendIcon,
  Assessment as AssessmentIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import { DocumentSet, Conflict, SubmissionResponse } from '../../features/cross-document/documentSetService';
import { useTranslation } from '../../store/languageStore';

interface DocumentCombinerProps {
  documentSet: DocumentSet;
  mergedData: any;
  conflicts: Conflict[];
  confidenceScore: number;
  onResolveConflict: (field: string, value: any) => void;
  onSubmitToAtlas?: (testMode: boolean, customReference?: string) => Promise<SubmissionResponse>;
  isLoading?: boolean;
}

export const DocumentCombiner: React.FC<DocumentCombinerProps> = ({
  documentSet,
  mergedData,
  conflicts,
  confidenceScore,
  onResolveConflict,
  onSubmitToAtlas,
  isLoading = false,
}) => {
  const { t, tString } = useTranslation();
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [resolvedConflicts, setResolvedConflicts] = useState<Record<string, any>>({});
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [testMode, setTestMode] = useState(true);
  const [customReference, setCustomReference] = useState('');
  const [submissionResult, setSubmissionResult] = useState<SubmissionResponse | null>(null);

  const steps = [
    tString('documentCombiner.steps.reviewData'),
    tString('documentCombiner.steps.resolveConflicts'),
    tString('documentCombiner.steps.validate'),
    tString('documentCombiner.steps.submit'),
  ];

  const handleNext = () => {
    if (activeStep === 1 && conflicts.length > 0) {
      // Check if all conflicts are resolved
      const unresolvedConflicts = conflicts.filter((c) => !resolvedConflicts[c.field]);
      if (unresolvedConflicts.length > 0) {
        alert(tString('documentCombiner.alerts.resolveAllConflicts'));
        return;
      }
    }
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleConflictResolution = (field: string, value: any) => {
    setResolvedConflicts((prev) => ({ ...prev, [field]: value }));
    onResolveConflict(field, value);
  };

  const handleEditField = (field: string, value: any) => {
    setEditingField(field);
    setEditValue(String(value || ''));
  };

  const handleSaveField = (field: string) => {
    onResolveConflict(field, editValue);
    setEditingField(null);
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (onSubmitToAtlas) {
      try {
        setIsSubmitting(true);
        const result = await onSubmitToAtlas(testMode, customReference);
        setSubmissionResult(result);
        if (result.success) {
          setActiveStep(4); // Move to completed state
        }
      } catch (error) {
        setSubmissionResult({ success: false, error: 'Submission failed unexpectedly' });
      } finally {
        setIsSubmitting(false);
        setSubmitDialogOpen(false);
      }
      return;
    }
    setSubmitDialogOpen(false);
  };

  const getConfidenceColor = (score: number) => {
    if (score >= 0.9) return 'success';
    if (score >= 0.7) return 'warning';
    return 'error';
  };

  const renderDataSection = (title: string, data: any) => {
    if (!data || Object.keys(data).length === 0) return null;

    return (
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">{title}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer>
            <Table size="small">
              <TableBody>
                {Object.entries(data).map(([key, value]) => (
                  <TableRow key={key}>
                    <TableCell sx={{ fontWeight: 500, width: '40%' }}>
                      {key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                    </TableCell>
                    <TableCell>
                      {editingField === key ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <TextField
                            size="small"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            fullWidth
                          />
                          <IconButton size="small" onClick={() => handleSaveField(key)}>
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => setEditingField(null)}>
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2">
                            {Array.isArray(value)
                              ? value.join(', ')
                              : typeof value === 'object'
                              ? JSON.stringify(value, null, 2)
                              : String(value)}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleEditField(key, value)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderConflicts = () => {
    if (conflicts.length === 0) {
      return (
        <Alert severity="success" icon={<CheckIcon />}>
          {t('documentCombiner.alerts.noConflicts')}
        </Alert>
      );
    }

    const pluralSuffix = conflicts.length > 1 ? 's have' : ' has';
    return (
      <Box>
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
          {t('documentCombiner.alerts.conflictsFound', conflicts.length, pluralSuffix)}
        </Alert>
        {conflicts.map((conflict) => (
          <Card key={conflict.field} sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                {conflict.field.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
              </Typography>
              <RadioGroup
                value={resolvedConflicts[conflict.field] || ''}
                onChange={(e) => handleConflictResolution(conflict.field, e.target.value)}
              >
                {conflict.values.map((option, index) => (
                  <Box key={index} sx={{ mb: 1, p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <FormControlLabel
                      value={option.value}
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body1">{String(option.value)}</Typography>
                          <Box display="flex" gap={1} mt={0.5}>
                            <Chip
                              label={`${tString('documentCombiner.conflict.from')}: ${option.source}`}
                              size="small"
                              variant="outlined"
                            />
                            <Chip
                              label={`${tString('documentCombiner.conflict.confidence')}: ${Math.round(option.confidence * 100)}%`}
                              size="small"
                              color={option.confidence > 0.8 ? 'success' : 'default'}
                            />
                          </Box>
                        </Box>
                      }
                    />
                  </Box>
                ))}
              </RadioGroup>
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  };

  // Count non-empty fields recursively for validation
  const countPopulatedFields = (data: any): { total: number; populated: number } => {
    let total = 0;
    let populated = 0;
    const walk = (obj: any) => {
      if (!obj || typeof obj !== 'object') return;
      if (Array.isArray(obj)) {
        obj.forEach(walk);
        return;
      }
      for (const [, v] of Object.entries(obj)) {
        if (v && typeof v === 'object' && !Array.isArray(v)) {
          walk(v);
        } else {
          total++;
          if (v !== null && v !== undefined && v !== '') populated++;
        }
      }
    };
    walk(data);
    return { total, populated };
  };

  const renderValidation = () => {
    const { total, populated } = countPopulatedFields(mergedData);
    const completeness = total > 0 ? Math.round((populated / total) * 100) : 0;
    const isValid = completeness >= 80; // At least 80% fields populated

    return (
      <Box>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <AssessmentIcon color="primary" />
          <Typography variant="h6">{t('documentCombiner.validation.title')}</Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  {t('documentCombiner.validation.confidenceScore')}
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                  <LinearProgress
                    variant="determinate"
                    value={confidenceScore * 100}
                    sx={{ flex: 1, height: 10, borderRadius: 5 }}
                    color={getConfidenceColor(confidenceScore)}
                  />
                  <Chip
                    label={`${Math.round(confidenceScore * 100)}%`}
                    color={getConfidenceColor(confidenceScore)}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  {t('documentCombiner.validation.requiredFields')}
                </Typography>
                {isValid ? (
                  <Alert severity="success" icon={<CheckIcon />}>
                    {t('documentCombiner.validation.allFieldsPresent')}
                  </Alert>
                ) : (
                  <Alert severity="warning">
                    {`${populated}/${total} fields populated (${completeness}%)`}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box mt={3}>
          <Typography variant="subtitle1" gutterBottom>
            {t('documentCombiner.validation.mergedDataSummary')}
          </Typography>
          <Paper sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {`${populated} of ${total} fields populated across ${Object.keys(mergedData || {}).length} sections`}
            </Typography>
          </Paper>
        </Box>

        <Box mt={3} display="flex" justifyContent="center">
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={() => setSubmitDialogOpen(true)}
            size="large"
          >
            {t('documentCombiner.actions.submitToAtlas')}
          </Button>
        </Box>
      </Box>
    );
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('documentCombiner.alerts.reviewMergedData', (documentSet.documents || []).length)}
            </Alert>
            {Object.entries(mergedData || {}).map(([key, value]) => {
              // Render nested objects as expandable sections
              if (value && typeof value === 'object' && !Array.isArray(value)) {
                return renderDataSection(
                  key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
                  value
                );
              }
              // Render arrays (e.g., positionen) as their own sections
              if (Array.isArray(value) && value.length > 0) {
                return (
                  <Box key={key}>
                    {value.map((item: any, idx: number) =>
                      typeof item === 'object'
                        ? renderDataSection(
                            `${key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())} #${idx + 1}`,
                            item
                          )
                        : null
                    )}
                  </Box>
                );
              }
              return null;
            })}
            {/* Fallback: show flat top-level scalars if any */}
            {(() => {
              const flatFields: Record<string, any> = {};
              Object.entries(mergedData || {}).forEach(([key, value]) => {
                if (value !== null && value !== undefined && typeof value !== 'object') {
                  flatFields[key] = value;
                }
              });
              return Object.keys(flatFields).length > 0
                ? renderDataSection(tString('documentCombiner.sections.generalData') || 'General Data', flatFields)
                : null;
            })()}
          </Box>
        );
      case 1:
        return renderConflicts();
      case 2:
        return renderValidation();
      case 3:
        return (
          <Box textAlign="center" py={4}>
            {submissionResult ? (
              submissionResult.success ? (
                <>
                  <CheckIcon sx={{ fontSize: 60, color: theme.palette.success.main }} />
                  <Typography variant="h5" gutterBottom>
                    {t('documentCombiner.submission.success')}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph>
                    {t('documentCombiner.submission.submissionId', submissionResult.submission_id)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {submissionResult.message}
                  </Typography>
                </>
              ) : (
                <>
                  <WarningIcon sx={{ fontSize: 60, color: theme.palette.error.main }} />
                  <Typography variant="h5" gutterBottom>
                    {t('documentCombiner.submission.failed')}
                  </Typography>
                  <Typography variant="body1" color="error" paragraph>
                    {submissionResult.error}
                  </Typography>
                  <Button variant="contained" onClick={() => setSubmitDialogOpen(true)}>
                    {t('documentCombiner.actions.tryAgain')}
                  </Button>
                </>
              )
            ) : (
              <LinearProgress />
            )}
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        {/* Header */}
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <MergeIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5">{t('documentCombiner.title')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {documentSet.name} - {(documentSet.documents || []).length} documents
            </Typography>
          </Box>
        </Box>

        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Content */}
        {isLoading ? (
          <Box textAlign="center" py={4}>
            <LinearProgress sx={{ mb: 2 }} />
            <Typography>{t('documentCombiner.processing')}</Typography>
          </Box>
        ) : (
          <>
            {renderStepContent()}

            {/* Navigation */}
            <Box display="flex" justifyContent="space-between" mt={4}>
              <Button disabled={activeStep === 0} onClick={handleBack}>
                {t('documentCombiner.actions.back')}
              </Button>
              {activeStep < 2 && (
                <Button variant="contained" onClick={handleNext}>
                  {t('documentCombiner.actions.next')}
                </Button>
              )}
            </Box>
          </>
        )}
      </Paper>

      {/* Submit Dialog */}
      <Dialog open={submitDialogOpen} onClose={() => setSubmitDialogOpen(false)}>
        <DialogTitle>{t('documentCombiner.submission.title')}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" paragraph>
              {t('documentCombiner.submission.chooseMode')}
            </Typography>
            <RadioGroup
              value={testMode ? 'test' : 'production'}
              onChange={(e) => setTestMode(e.target.value === 'test')}
            >
              <FormControlLabel value="test" control={<Radio />} label={tString('documentCombiner.submission.testMode')} />
              <FormControlLabel value="production" control={<Radio />} label={tString('documentCombiner.submission.production')} />
            </RadioGroup>
            <TextField
              fullWidth
              label={tString('documentCombiner.submission.customReference')}
              value={customReference}
              onChange={(e) => setCustomReference(e.target.value)}
              margin="normal"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubmitDialogOpen(false)} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmitting}
            startIcon={<UploadIcon />}
          >
            {t('documentCombiner.steps.submit')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentCombiner;